// frontend/src/ui/views/ViewGLLines/lineItemQuery.ts
import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import type { TApiLineItemFilterOptions, TApiLineItemQuery } from "@/apis/proda_finance/types";
import { STRING_MULTI_SELECT_BLANK } from "@/utils/tableMultiSelectFilter";
import {
    textParam,
    numberRangeParam,
    numberRangeMultiSelectParam,
    dateRangeParam,
    multiSelectParam,
    sortParam,
} from "@/useCases/serverTableQuery";

// Column id → the scalar text query key it drives.
const TEXT_PARAM: Record<string, keyof TApiLineItemQuery> = {
    accountName: "account_name",
    sourceAccountName: "account_name",
    accountCode: "account_code",
    mappedAccountName: "mapped_account_name",
    breAccountName: "mapped_account_name",
    mappedAccountCode: "mapped_account_code",
    mappedAccountNumber: "mapped_account_code",
    description: "description",
    reference: "reference",
    ref: "reference",
};

// Column id → [from key, to key, selected key] for the File Review account
// number columns (from-to range popover with an "or select values" list).
const ACCOUNT_CODE_PARAM: Record<
    string,
    [keyof TApiLineItemQuery, keyof TApiLineItemQuery, keyof TApiLineItemQuery]
> = {
    sourceAccountNumber: ["account_code_from", "account_code_to", "account_codes"],
    breAccountNumber: [
        "mapped_account_code_from",
        "mapped_account_code_to",
        "mapped_account_codes",
    ],
};

// Column id → [min key, max key] for numberRange columns.
const NUMBER_PARAM: Record<string, [keyof TApiLineItemQuery, keyof TApiLineItemQuery]> = {
    amount: ["amount_min", "amount_max"],
    lineNumber: ["line_number_min", "line_number_max"],
    rowNumber: ["line_number_min", "line_number_max"],
    trannum: ["trannum_min", "trannum_max"],
};

// Column id → [from key, to key] for date columns.
const DATE_PARAM: Record<string, [keyof TApiLineItemQuery, keyof TApiLineItemQuery]> = {
    transactionDate: ["transaction_date_from", "transaction_date_to"],
    postingDate: ["posting_date_from", "posting_date_to"],
    postMonth: ["posting_date_from", "posting_date_to"],
};

// Column id → repeated-array param for multiSelect columns.
const MULTI_PARAM: Record<string, keyof TApiLineItemQuery> = {
    bookNumber: "book_numbers",
    sourceAccountNumber2: "account_code_2s",
    propertyCode: "property_codes",
    localPropcode: "property_codes",
    mappedPropertyCode: "mapped_property_codes",
    propcode: "mapped_property_codes",
    segment1: "segment_1",
    segment2: "segment_2",
    segment3: "segment_3",
    segment4: "segment_4",
    segment5: "segment_5",
    segment6: "segment_6",
    segment7: "segment_7",
    isValid: "validation_status",
    validationStatus: "validation_status",
    displayType: "display_types",
    localCurrency: "currencies",
};

/** True when at least one column filter carries a non-empty value. */
export function hasAnyLineItemColumnFilter(filters: ColumnFiltersState): boolean {
    return filters.some(({ value }) => {
        if (value == null) return false;
        if (typeof value === "string") return value.trim() !== "";
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === "object") {
            const r = value as { from?: string; to?: string; selected?: string[] };
            return (r.from ?? "") !== "" || (r.to ?? "") !== "" || (r.selected?.length ?? 0) > 0;
        }
        return true;
    });
}

/** Map TanStack column filters + sorting into the line-items API query. Pure
    and referentially stable; safe to pass to useServerTable.toQuery. */
export function lineItemColumnFiltersToQuery(
    filters: ColumnFiltersState,
    sorting: SortingState,
): TApiLineItemQuery {
    const q: TApiLineItemQuery = {};

    for (const id of Object.keys(TEXT_PARAM)) {
        const key = TEXT_PARAM[id]!;
        const v = textParam(filters, id);
        if (v !== undefined) (q as Record<string, unknown>)[key] = v;
    }

    for (const id of Object.keys(ACCOUNT_CODE_PARAM)) {
        const [fromKey, toKey, selectedKey] = ACCOUNT_CODE_PARAM[id]!;
        const r = numberRangeMultiSelectParam(filters, id);
        if (r) {
            if (r.from !== undefined) (q as Record<string, unknown>)[fromKey] = r.from;
            if (r.to !== undefined) (q as Record<string, unknown>)[toKey] = r.to;
            if (r.selected !== undefined) (q as Record<string, unknown>)[selectedKey] = r.selected;
        }
    }

    for (const id of Object.keys(NUMBER_PARAM)) {
        const [minKey, maxKey] = NUMBER_PARAM[id]!;
        const r = numberRangeParam(filters, id);
        if (r) {
            if (r.min !== undefined) (q as Record<string, unknown>)[minKey] = r.min;
            if (r.max !== undefined) (q as Record<string, unknown>)[maxKey] = r.max;
        }
    }

    for (const id of Object.keys(DATE_PARAM)) {
        const [fromKey, toKey] = DATE_PARAM[id]!;
        const r = dateRangeParam(filters, id);
        if (r) {
            if (r.from !== undefined) (q as Record<string, unknown>)[fromKey] = r.from;
            if (r.to !== undefined) (q as Record<string, unknown>)[toKey] = r.to;
        }
    }

    for (const id of Object.keys(MULTI_PARAM)) {
        const key = MULTI_PARAM[id]!;
        const arr = multiSelectParam(filters, id);
        if (arr) (q as Record<string, unknown>)[key] = arr;
    }

    Object.assign(q, sortParam(sorting));

    return q;
}

const NO_SORTING: SortingState = [];

/** The filters-only query used to scope the facet (filter-options) request.

    Facets are narrowed by the whole active filter group, so this is the same
    mapping the list query uses minus sorting and pagination — neither changes
    which values exist, and including them would refetch the facets for nothing. */
export function lineItemFacetQuery(filters: ColumnFiltersState): TApiLineItemQuery {
    return lineItemColumnFiltersToQuery(filters, NO_SORTING);
}

// Facet list → the query param whose selection it offers values for. Used to keep
// an applied value listed even after another filter narrows it away.
const FACET_SELECTION_PARAM: Record<keyof TApiLineItemFilterOptions, keyof TApiLineItemQuery> = {
    account_codes: "account_codes",
    account_code_2s: "account_code_2s",
    mapped_account_codes: "mapped_account_codes",
    book_numbers: "book_numbers",
    property_codes: "property_codes",
    mapped_property_codes: "mapped_property_codes",
    segment_1: "segment_1",
    segment_2: "segment_2",
    segment_3: "segment_3",
    segment_4: "segment_4",
    segment_5: "segment_5",
    segment_6: "segment_6",
    segment_7: "segment_7",
    display_types: "display_types",
    currencies: "currencies",
    validation_statuses: "validation_status",
};

/**
 * Add each dimension's own applied values back into its (server-narrowed) facet list.
 *
 * The server narrows every facet by the *other* active filters, so a value that is
 * currently applied can drop out of its own list — e.g. propcode PC-001 is selected
 * and then Book 2, which holds no PC-001 rows. Without it in the list the checkbox
 * disappears and the filter can no longer be cleared from the popover, so the user
 * is stuck with an invisible filter. Merging it back keeps every applied value
 * visible and deselectable.
 *
 * The blank sentinel is skipped: columns that support it pin their own "(Blanks)"
 * option, and it is not a real column value.
 */
export function withSelectedFacetValues(
    facets: TApiLineItemFilterOptions | null,
    columnFilters: ColumnFiltersState,
): TApiLineItemFilterOptions | null {
    if (!facets) return null;
    const query = lineItemFacetQuery(columnFilters);
    const merged = { ...facets };
    for (const [field, param] of Object.entries(FACET_SELECTION_PARAM) as [
        keyof TApiLineItemFilterOptions,
        keyof TApiLineItemQuery,
    ][]) {
        const selected = query[param];
        if (!Array.isArray(selected) || selected.length === 0) continue;
        const values = new Set(facets[field]);
        const before = values.size;
        for (const value of selected) {
            if (value !== STRING_MULTI_SELECT_BLANK) values.add(value);
        }
        if (values.size !== before) merged[field] = [...values].sort();
    }
    return merged;
}
